import {
  BOOK_FORMATS,
  FORMAT_MIME_TYPES,
  MAX_PENDING_UPLOADS_PER_USER,
  UPLOAD_EXTENSIONS,
} from "@shelflist/shared";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CircleAlert,
  CircleCheck,
  CloudUpload,
  FileText,
  LoaderCircle,
  RotateCw,
  X,
} from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { Page } from "@/components/page";
import { Button } from "@/components/ui/button";
import { useErrorText } from "@/lib/errors";
import { type QueueItem, useUploadQueue } from "@/lib/upload-queue";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/upload/")({
  component: UploadPage,
});

/** react-dropzone `accept` map: MIME type -> extensions (browsers often send no type for ebooks). */
const ACCEPT: Record<string, string[]> = {};
for (const format of BOOK_FORMATS) {
  const mime = FORMAT_MIME_TYPES[format];
  const extensions = ACCEPT[mime] ?? [];
  extensions.push(`.${format}`);
  if (format === "kepub") extensions.push(".kepub.epub");
  ACCEPT[mime] = extensions;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadPage() {
  const { t } = useTranslation("upload");
  const { items, addFiles, retry, remove, clearFinished } = useUploadQueue();
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPT,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted, rejections) =>
      addFiles(
        accepted,
        rejections.map((r) => r.file),
      ),
  });
  const hasFinished = items.some(
    (item) =>
      item.status === "created" || item.status === "needs_decision" || item.status === "duplicate",
  );

  return (
    <Page
      title={t("title")}
      description={t("description", {
        formats: BOOK_FORMATS.map((f) => f.toUpperCase()).join(", "),
      })}
    >
      <div
        {...getRootProps()}
        data-testid="dropzone"
        className={cn(
          "flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors",
          isDragActive ? "border-primary bg-accent" : "border-border",
        )}
      >
        <input {...getInputProps()} aria-label={t("dropzone.input")} data-testid="file-input" />
        <CloudUpload className="text-muted-foreground size-10" aria-hidden />
        <p className="font-medium">{isDragActive ? t("dropzone.active") : t("dropzone.idle")}</p>
        <p className="text-muted-foreground text-sm">{t("dropzone.or")}</p>
        <Button onClick={open}>{t("dropzone.choose")}</Button>
        <p className="text-muted-foreground text-xs">{UPLOAD_EXTENSIONS.join(" ")}</p>
      </div>

      {items.length > 0 ? (
        <section aria-labelledby="queue-title">
          <div className="mb-3 flex items-center justify-between">
            <h2 id="queue-title" className="text-lg font-semibold">
              {t("queue.title", { count: items.length })}
            </h2>
            {hasFinished ? (
              <Button variant="ghost" size="sm" onClick={clearFinished}>
                {t("queue.clearFinished")}
              </Button>
            ) : null}
          </div>
          <ul className="divide-y rounded-lg border">
            {items.map((item) => (
              <QueueRow key={item.id} item={item} onRetry={retry} onRemove={remove} />
            ))}
          </ul>
        </section>
      ) : null}
    </Page>
  );
}

function QueueRow({
  item,
  onRetry,
  onRemove,
}: {
  item: QueueItem;
  onRetry: (id: number) => void;
  onRemove: (id: number) => void;
}) {
  const { t } = useTranslation("upload");
  const { t: tErrors } = useTranslation("errors");
  const errorText = useErrorText();
  const busy = item.status === "uploading" || item.status === "processing";
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3" data-status={item.status}>
      <FileText className="text-muted-foreground size-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{item.file.name}</div>
        <div className="text-muted-foreground text-sm">{formatSize(item.file.size)}</div>
        {item.status === "uploading" || item.status === "processing" ? (
          <div
            role="progressbar"
            aria-label={item.file.name}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={item.progress}
            className="bg-muted mt-2 h-1.5 w-full overflow-hidden rounded-full"
          >
            <div
              className={cn(
                "bg-primary h-full transition-[width]",
                item.status === "processing" && "animate-pulse",
              )}
              style={{ width: `${item.progress}%` }}
            />
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2 text-sm">
        {busy ? <LoaderCircle className="size-4 animate-spin" aria-hidden /> : null}
        {item.status === "created" || item.status === "needs_decision" ? (
          <CircleCheck className="size-4 text-green-600" aria-hidden />
        ) : null}
        {item.status === "duplicate" || item.status === "error" ? (
          <CircleAlert className="text-destructive size-4" aria-hidden />
        ) : null}
        <span className={cn(item.status === "error" && "text-destructive")}>
          {item.status === "queued" ? t("status.queued") : null}
          {item.status === "uploading" ? t("status.uploading", { percent: item.progress }) : null}
          {item.status === "processing" ? t("status.processing") : null}
          {item.status === "created" ? t("status.created") : null}
          {item.status === "needs_decision" ? t("status.needsDecision") : null}
          {item.status === "duplicate" ? t("status.duplicate") : null}
          {item.status === "error"
            ? item.errorCode === "pending_upload_limit"
              ? tErrors("pending_upload_limit", {
                  limit: item.limit ?? MAX_PENDING_UPLOADS_PER_USER,
                })
              : errorText(item.errorCode)
            : null}
        </span>
        {item.status === "created" && item.bookId ? (
          <a className="text-primary underline underline-offset-4" href={`/books/${item.bookId}`}>
            {t("actions.openBook")}
          </a>
        ) : null}
        {item.status === "duplicate" && item.bookId ? (
          <a className="text-primary underline underline-offset-4" href={`/books/${item.bookId}`}>
            {t("actions.viewExisting")}
          </a>
        ) : null}
        {item.status === "needs_decision" ||
        (item.status === "error" && item.errorCode === "pending_upload_limit") ? (
          <Link className="text-primary underline underline-offset-4" to="/upload/inbox">
            {t("actions.openInbox")}
          </Link>
        ) : null}
        {item.status === "error" ? (
          <Button size="sm" variant="outline" onClick={() => onRetry(item.id)}>
            <RotateCw aria-hidden />
            {t("actions.retry")}
          </Button>
        ) : null}
        {!busy ? (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t("actions.remove", { name: item.file.name })}
            onClick={() => onRemove(item.id)}
          >
            <X aria-hidden />
          </Button>
        ) : null}
      </div>
    </li>
  );
}
